import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Alert, useTheme, CircularProgress } from '@mui/material';

import Button from '../../../common/buttons/Button';
import { DotsStepper } from '../../../common/DotsStepper';
import { AppDispatch, RootState } from '../../../../data/store';
import { PaperHeader } from '../../../common/layout/Paper';

import {
	InternalFormValues,
	InternalOnboardingStatus,
	InternalOnboardingStep,
} from './types';
import { steps } from './constants';
import BasicInformationStep from './BasicInformationStep';
import WorkPersonalPreferencesStep from './WorkPersonalPreferencesStep';
import TravelInformationStep from './TravelInformationStep';
import ReviewStep from './ReviewStep';
import { useCrewForm } from '../../hooks/useCrewForm';
import {
	buildInternalSavePayload,
	buildInternalSubmitPayload,
	createInternalDefaultValues,
	mapUserToInternalFormValues,
} from './formUtils';
import OrbitoLogo from '../../../../images/Orbito_Logo_WhiteText.svg';
import OrbitoLogoDarkText from '../../../../images/Orbito_Logo_DarkText.svg';
import {
	ContentContainer,
	ScrollableContent,
	StickyHeader,
	FixedButtonsContainer,
	Container,
	StyledPaper,
} from './styles';
import { UpdateSelfDTO, User, UserStatus } from '../../../../data/models/user';
import { logout } from '../../../../data/stores/authStore';
import { updateSelf } from '../../../../services/user-service';
import { showAlert } from '../../../../data/stores/alertStore';
import { fetchSelf } from '../../../../data/thunks/userThunk';

export default function InternalOnboardingScreen() {
	const dispatch = useDispatch<AppDispatch>();
	const scrollableRef = React.useRef<HTMLDivElement | null>(null);
	const theme = useTheme();
	const isLightMode = theme?.palette?.mode === 'light';
	const navigate = useNavigate();

	// Redux selectors for user
	const { user } = useSelector((state: RootState) => state.authState);

	const [status, setStatus] = useState<InternalOnboardingStatus>(
		InternalOnboardingStatus.FORM
	);
	const [currentStep, setCurrentStep] = useState<InternalOnboardingStep>(
		InternalOnboardingStep.BASIC_INFO
	);
	const [error, setError] = useState<string>('');

	// Use custom hook for form logic
	const internalDefaultValues = React.useMemo(
		() => createInternalDefaultValues(),
		[]
	);

	const handleInternalSave = React.useCallback(
		async (payload: UpdateSelfDTO | null | undefined) => {
			if (!payload || Object.keys(payload).length === 0) {
				return;
			}
			await updateSelf(payload);
		},
		[]
	);

	const handleInternalSubmit = React.useCallback(
		async (
			payload: UpdateSelfDTO,
			{
				setStatus: setFormStatus,
				setError: setFormError,
			}: {
				setStatus: (status: InternalOnboardingStatus) => void;
				setError: (error: string) => void;
			}
		) => {
			setFormStatus(InternalOnboardingStatus.SUBMITTING);
			try {
				await updateSelf(payload);
				setFormStatus(InternalOnboardingStatus.SUCCESS);
				dispatch(fetchSelf());
				dispatch(
					showAlert({
						message: 'Onboarding completed successfully!',
						severity: 'success',
					})
				);
			} catch (err) {
				console.error('Error submitting onboarding form:', err);
				setFormError('Failed to submit form. Please try again.');
				setFormStatus(InternalOnboardingStatus.FORM);
			}
		},
		[dispatch]
	);

	const {
		control,
		errors,
		setValue,
		formValues,
		trigger,
		saveProgress,
		submitForm,
		hydrateForm,
	} = useCrewForm<
		InternalFormValues,
		User,
		UpdateSelfDTO,
		UpdateSelfDTO,
		void,
		{
			setStatus: (status: InternalOnboardingStatus) => void;
			setError: (error: string) => void;
		}
	>({
		defaultValues: internalDefaultValues,
		mode: 'onSubmit',
		reValidateMode: 'onBlur',
		hydrate: mapUserToInternalFormValues,
		buildSavePayload: (values, helpers) =>
			buildInternalSavePayload(values, helpers),
		onSave: handleInternalSave,
		buildSubmitPayload: buildInternalSubmitPayload,
		onSubmit: handleInternalSubmit,
	});

	useEffect(() => {
		if (typeof document === 'undefined') {
			return;
		}
		const previousBodyOverflow = document.body.style.overflow;
		const previousDocumentOverflow =
			document.documentElement.style.overflow;
		document.body.style.overflow = 'hidden';
		document.documentElement.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previousBodyOverflow;
			document.documentElement.style.overflow = previousDocumentOverflow;
		};
	}, []);

	// Hydrate form with user data on mount
	useEffect(() => {
		if (user) {
			hydrateForm(user);
		}
	}, [user, hydrateForm]);

	// Ensure the scrollable content resets to top on step change
	useEffect(() => {
		if (scrollableRef.current) {
			scrollableRef.current.scrollTo({ top: 0, behavior: 'auto' });
		}
	}, [currentStep]);

	// Navigation handlers
	const handleNext = async () => {
		// Save current progress before moving to next step
		const saved = await saveProgress();
		if (!saved) {
			setError(
				'Failed to save your progress. Your changes may not be saved. You can continue, but please try again or contact support if this persists.'
			);
		}

		if (currentStep < InternalOnboardingStep.REVIEW) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handleBack = () => {
		if (currentStep > InternalOnboardingStep.BASIC_INFO) {
			setCurrentStep(currentStep - 1);
		}
	};

	const onFormSubmit = async () => {
		await submitForm({ setStatus, setError });
	};

	const renderContent = () => {
		switch (status) {
			case InternalOnboardingStatus.FORM:
				return (
					<ContentContainer>
						{/* Scrollable Content Area */}
						<ScrollableContent ref={scrollableRef}>
							{error && (
								<Alert severity="error" sx={{ mb: 2 }}>
									{error}
								</Alert>
							)}

							{/* Step Content */}
							{currentStep ===
								InternalOnboardingStep.BASIC_INFO && (
								<BasicInformationStep
									formValues={formValues}
									setValue={setValue}
									control={control}
									errors={errors}
									isRequired={true}
								/>
							)}
							{currentStep ===
								InternalOnboardingStep.WORK_PERSONAL_PREFERENCES && (
								<WorkPersonalPreferencesStep
									formValues={formValues}
									setValue={setValue}
									control={control}
									errors={errors}
									isRequired={true}
								/>
							)}
							{currentStep ===
								InternalOnboardingStep.TRAVEL_INFO && (
								<TravelInformationStep
									formValues={formValues}
									setValue={setValue}
									control={control}
									errors={errors}
									isRequired={true}
								/>
							)}
							{currentStep === InternalOnboardingStep.REVIEW && (
								<ReviewStep
									formValues={formValues}
									control={control}
									setValue={setValue}
									errors={errors}
								/>
							)}
						</ScrollableContent>

						<FixedButtonsContainer>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'flex-end',
									alignItems: 'center',
									gap: 2,
									width: '100%',
								}}
							>
								{/* Navigation buttons */}
								<Box sx={{ display: 'flex', gap: 2 }}>
									{currentStep >
										InternalOnboardingStep.BASIC_INFO && (
										<Button
											variant="outlined"
											onClick={handleBack}
										>
											Back
										</Button>
									)}
									{currentStep <
									InternalOnboardingStep.REVIEW ? (
										<Button
											variant="contained"
											onClick={handleNext}
										>
											NEXT
										</Button>
									) : (
										<Button
											variant="contained"
											onClick={onFormSubmit}
										>
											SUBMIT
										</Button>
									)}
								</Box>
							</Box>
						</FixedButtonsContainer>
					</ContentContainer>
				);

			case InternalOnboardingStatus.SUBMITTING:
				return (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							gap: 3,
						}}
					>
						<CircularProgress size={60} />
						<Box sx={{ textAlign: 'center' }}>
							<PaperHeader>
								Submitting your information...
							</PaperHeader>
						</Box>
					</Box>
				);

			case InternalOnboardingStatus.SUCCESS:
				return (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							gap: 3,
							textAlign: 'center',
							px: 4,
						}}
					>
						<Box
							sx={{
								fontSize: '64px',
								color: 'primary.main',
							}}
						>
							✓
						</Box>
						<PaperHeader>Onboarding Complete!</PaperHeader>
						<Box sx={{ color: 'text.secondary', mb: 2 }}>
							Thank you for completing your onboarding.{' '}
							{user?.status === UserStatus.UNSUBSCRIBED.id && (
								<>
									Please click below to secureley log out of
									the application.
								</>
							)}
						</Box>
						{user?.status === UserStatus.SUBSCRIBED.id ? (
							<Button
								variant="contained"
								onClick={() => navigate('/')}
							>
								Go to Dashboard
							</Button>
						) : (
							<Button
								variant="contained"
								onClick={() => {
									dispatch(logout());
									navigate('/login');
								}}
							>
								Logout
							</Button>
						)}
					</Box>
				);

			case InternalOnboardingStatus.ERROR:
				return (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							gap: 3,
							textAlign: 'center',
							px: 4,
						}}
					>
						<Box
							sx={{
								fontSize: '64px',
								color: 'error.main',
							}}
						>
							✗
						</Box>
						<PaperHeader>Onboarding Error</PaperHeader>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
					</Box>
				);

			default:
				return null;
		}
	};

	return (
		<Container>
			<Box sx={{ position: 'absolute', top: '20px', left: '20px' }}>
				<img
					src={isLightMode ? OrbitoLogoDarkText : OrbitoLogo}
					style={{ height: 30 }}
					onClick={() => navigate('/')}
				/>
			</Box>
			<StyledPaper gradientBorder>
				<Box
					sx={{
						width: '100%',
						borderRadius: 2,
						position: 'relative',
						flex: 1,
						overflow: 'hidden',
						height: 'calc(100vh - 120px)',
						display: 'flex',
						flexDirection: 'column',
						backgroundColor: '#1C1C1C',
					}}
				>
					{/* Sticky Header */}
					<StickyHeader>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								width: '100%',
							}}
						>
							<PaperHeader>Employee Staff Form</PaperHeader>
						</Box>
						{status === InternalOnboardingStatus.FORM && (
							<Box
								sx={{
									flex: 1,
									mt: 2,
								}}
							>
								<DotsStepper
									totalSteps={4}
									activeStep={currentStep}
									showButtons={false}
									stepLabels={steps}
									onStepClick={(stepIndex) =>
										setCurrentStep(stepIndex)
									}
								/>
							</Box>
						)}
					</StickyHeader>

					{/* Content Area */}
					<Box
						sx={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							overflow: 'hidden',
						}}
					>
						{renderContent()}
					</Box>
				</Box>
			</StyledPaper>
		</Container>
	);
}
